import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Toaster } from 'sonner';
import { store } from '@/store';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PublicRoute } from './components/auth/PublicRoute';
import { AppShell } from './components/layout/AppShell';
import ThemeToggle from './components/ui/ThemeToggle';

import DashboardPage from './pages/DashboardPage';
import QuizzesPage from './pages/QuizzesPage';
import NewQuizPage from './pages/NewQuizPage';
import QuestionsPage from './pages/QuestionsPage';
import NewQuestionPage from './pages/NewQuestionPage';
import RoomsPage from './pages/RoomsPage';
import BulkPage from './pages/BulkPage';
import AdminPage from './pages/AdminPage';
import AuthPage from './pages/AuthPage';
import ConsolePage from './pages/ConsolePage';
import PlayersPage from './pages/PlayersPage';
import TeamsPage from './pages/TeamsPage';
import PlayerDetailPage from './pages/PlayerDetailPage';
import PlayerEditPage from './pages/PlayerEditPage';
import PlayerReportPage from './pages/PlayerReportPage';
import ScenariosCorporatePage from './pages/ScenariosCorporatePage';
import ScenarioCreatePage from './pages/ScenarioCreatePage';
import ScenarioEditPage from './pages/ScenarioEditPage';
import ScenariosActivesPage from './pages/ScenariosActivesPage';
import ScenarioDetailPage from './pages/ScenarioDetailPage';
import ExerciseRunSessionPage from './pages/ExerciseRunSessionPage';
import ReportDetailPage from './pages/ReportDetailPage';
import MitreAttackPage from './pages/MitreAttackPage';
import NotificationsPage from './pages/NotificationsPage';
import FlagsPage from './pages/FlagsPage';
import PhasesPage from './pages/PhasesPage';
import InjectsPage from './pages/InjectsPage';
import ExerciseImmutableArchiveReportPage from './pages/ExerciseImmutableArchiveReportPage';
import ExerciseArchivesPage from './pages/ExerciseArchivesPage';
import CategoriesPage from './pages/CategoriesPage';
import NotFoundPage from './pages/NotFoundPage';

/** Legacy `/active-scenarios/corporate/:id/details` → `/scenarios/active/:id` */
function RedirectCorporateActiveDetailsToRun() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/scenarios/active/${id}`} replace />;
}

/** Legacy `/active-scenarios/corporate/:id/console` → `/scenarios/active/:id/console` */
function RedirectCorporateActiveConsoleToRun() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  return <Navigate to={`/scenarios/active/${id}/console${search}`} replace />;
}

function App() {
  return (
    <Provider store={store}>
      <>
        <Toaster richColors position="top-right" closeButton />
        <BrowserRouter>
          <div className="fixed top-4 right-4 z-[100]" aria-hidden="false">
            <ThemeToggle />
          </div>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              <Route
                path="/auth"
                element={
                  <PublicRoute>
                    <AuthPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Routes>
                        <Route path="/" element={<DashboardPage />} />
                        <Route path="/players" element={<PlayersPage />} />
                        <Route path="/teams" element={<TeamsPage />} />
                        <Route path="/players/:id" element={<PlayerDetailPage />} />
                        <Route path="/players/:id/edit" element={<PlayerEditPage />} />
                        <Route path="/players/:id/report" element={<PlayerReportPage />} />
                        <Route path="/scenarios" element={<ScenariosCorporatePage />} />
                        <Route path="/scenarios/new" element={<ScenarioCreatePage />} />
                        <Route path="/scenarios/:id/edit" element={<ScenarioEditPage />} />
                        <Route path="/scenarios/actives" element={<ScenariosActivesPage />} />
                        <Route
                          path="/scenarios/corporate/actives"
                          element={<Navigate to="/scenarios/actives" replace />}
                        />
                        {/* Immutable relational exercise archive report (not legacy Mongo archive) */}
                        <Route
                          path="/scenarios/exercise-archive/:archiveUuid/report"
                          element={<ExerciseImmutableArchiveReportPage />}
                        />
                        {/* Immutable relational exercise archive list */}
                        <Route path="/scenarios/reports" element={<ExerciseArchivesPage />} />
                        <Route path="/scenarios/archives" element={<Navigate to="/scenarios/reports" replace />} />
                        <Route path="/scenarios/active/:id/console" element={<ConsolePage />} />
                        <Route path="/scenarios/active/:activeId" element={<ExerciseRunSessionPage />} />
                        <Route
                          path="/scenarios/exercise-run/:exerciseId/:runPk"
                          element={<Navigate to="/scenarios/actives" replace />}
                        />
                        <Route path="/scenarios/:id/detail" element={<ScenarioDetailPage />} />
                        <Route
                          path="/active-scenarios/corporate/:id/details"
                          element={<RedirectCorporateActiveDetailsToRun />}
                        />
                        <Route
                          path="/active-scenarios/corporate/:id/console"
                          element={<RedirectCorporateActiveConsoleToRun />}
                        />
                        <Route path="/report/:report_id" element={<ReportDetailPage />} />
                        <Route path="/mitre-attack-automation" element={<MitreAttackPage />} />
                        <Route path="/notifications" element={<NotificationsPage />} />
                        <Route path="/categories" element={<CategoriesPage />} />
                        <Route path="/flags" element={<FlagsPage />} />
                        <Route path="/phases" element={<PhasesPage />} />
                        <Route path="/injects" element={<InjectsPage />} />
                        <Route path="/quizzes" element={<QuizzesPage />} />
                        <Route path="/quizzes/new" element={<NewQuizPage />} />
                        <Route path="/questions" element={<QuestionsPage />} />
                        <Route path="/questions/new" element={<NewQuestionPage />} />
                        <Route path="/rooms" element={<RoomsPage />} />
                        <Route path="/bulk" element={<BulkPage />} />
                        <Route path="/admin" element={<AdminPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </AppShell>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </>
    </Provider>
  );
}

export default App;
